import { logDiyPlaybookClone, summarizeDiyCloneResponse } from '@/lib/diyPlaybookCloneLog';
import { logAccountInteraction } from '@/components/utils/analytics';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

/**
 * POST /api/diy-playbooks/:id/duplicate — clones playbook definition + step assets.
 * @returns {Promise<{ ok: true, newPlaybookId: string, summary: object } | { ok: false, reason: string, status?: number, errorBody?: unknown, message?: string }>}
 */
export async function cloneDiyPlaybook({
  playbookId,
  tenantId,
  userId,
  cloneSource = 'diy_clone',
  duplicateEndpoint = null,
  playbookName = null,
  sourceOwnerUserId = null,
}) {
  const pid = playbookId != null ? String(playbookId).trim() : '';
  const tid = tenantId != null ? String(tenantId).trim() : '';
  const uid = userId != null ? String(userId).trim() : '';
  const url =
    duplicateEndpoint ||
    (pid ? `${API_BASE_URL}/api/diy-playbooks/${encodeURIComponent(pid)}/duplicate` : null);

  if (!pid || !tid || !uid || !url) {
    logDiyPlaybookClone('invalid_context', {
      source: cloneSource,
      sourcePlaybookId: pid || null,
      tenantId: tid || null,
      editorUserId: uid || null,
    });
    return { ok: false, reason: 'missing_context' };
  }

  logDiyPlaybookClone('start', {
    source: cloneSource,
    sourcePlaybookId: pid,
    sourceOwnerUserId: sourceOwnerUserId || null,
    tenantId: tid,
    editorUserId: uid,
    url,
    usedCustomDuplicateEndpoint: Boolean(duplicateEndpoint),
    playbookName,
  });

  try {
    const res = await fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'X-User-ID': uid,
        'X-Tenant-ID': tid,
      },
      body: JSON.stringify({ tenant_id: tid, user_id: uid }),
    });

    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      logDiyPlaybookClone('http_error', {
        source: cloneSource,
        sourcePlaybookId: pid,
        status: res.status,
        statusText: res.statusText,
        errorBody,
      });
      return { ok: false, reason: 'http_error', status: res.status, errorBody };
    }

    const data = await res.json();
    const summary = summarizeDiyCloneResponse(data);
    const newId = summary.newPlaybookId;
    logDiyPlaybookClone('success', {
      ...summary,
      source: cloneSource,
      sourcePlaybookId: pid,
      assetsBundled: summary.apiAssetsBundled,
    });

    if (!newId) {
      logDiyPlaybookClone('http_error', {
        source: cloneSource,
        sourcePlaybookId: pid,
        message: 'duplicate_ok_but_missing_new_playbook_id',
        responseKeys: data && typeof data === 'object' ? Object.keys(data) : [],
      });
      return { ok: false, reason: 'missing_new_id' };
    }

    void logAccountInteraction({
      tenantId: tid,
      userId: uid,
      playbookId: pid,
      source: 'diy',
      interactionType: 'playbook_cloned',
      metadata: {
        new_playbook_id: String(newId),
        clone_source: cloneSource,
        playbook_name: playbookName,
      },
    }).catch((err) => console.error('Analytics error:', err));

    return { ok: true, newPlaybookId: String(newId), summary, data };
  } catch (err) {
    logDiyPlaybookClone('network_error', {
      source: cloneSource,
      sourcePlaybookId: pid,
      message: err?.message ?? String(err),
    });
    return { ok: false, reason: 'network_error', message: err?.message };
  }
}
